import type {
  CustomerCompanyWorkflowSchedule,
  WorkflowFrequencyType,
} from "services/customerCompanyAdminService";

/**
 * Formatting helpers shared by the CustomerCompany detail tabs.
 * Pure functions only — no React, no services — so they stay unit-testable.
 */

/** Locale-formatted date-time, or an em dash when there is no value. */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Human file size, e.g. 2.4 MB. Returns an em dash for null/unknown sizes. */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** "14:30:00" → "2:30 PM". Tolerates "14:30" and malformed input. */
export const formatTimeOfDay = (timeOfDay: string): string => {
  const [rawHours, rawMinutes] = (timeOfDay ?? "").split(":");
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeOfDay ?? "";

  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHour}:${String(minutes).padStart(2, "0")} ${period}`;
};

const ordinal = (day: number): string => {
  const remainderTen = day % 10;
  const remainderHundred = day % 100;
  if (remainderTen === 1 && remainderHundred !== 11) return `${day}st`;
  if (remainderTen === 2 && remainderHundred !== 12) return `${day}nd`;
  if (remainderTen === 3 && remainderHundred !== 13) return `${day}rd`;
  return `${day}th`;
};

/**
 * One-line description of when a schedule fires, e.g.
 * "Weekly on Monday at 9:00 AM (Europe/Berlin)".
 *
 * Staff cannot edit schedules from the admin app — the widget application token that goes into the
 * EventBridge payload is not recoverable server-side — so schedules are rendered read-only and
 * this is how they are described.
 */
export const describeSchedule = (
  schedule: Pick<
    CustomerCompanyWorkflowSchedule,
    "frequencyType" | "timeOfDay" | "dayOfWeek" | "dayOfMonth" | "timezone"
  >,
): string => {
  const time = formatTimeOfDay(schedule.timeOfDay);
  const timezone = schedule.timezone ? ` (${schedule.timezone})` : "";

  const cadence: Record<WorkflowFrequencyType, string> = {
    Daily: "Daily",
    Weekdays: "Every weekday",
    Weekly:
      schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined
        ? `Weekly on ${DAY_NAMES[schedule.dayOfWeek] ?? "—"}`
        : "Weekly",
    Monthly:
      schedule.dayOfMonth !== null && schedule.dayOfMonth !== undefined
        ? `Monthly on the ${ordinal(schedule.dayOfMonth)}`
        : "Monthly",
  };

  return `${cadence[schedule.frequencyType] ?? schedule.frequencyType} at ${time}${timezone}`;
};
